// <knack-faq> — progressive enhancement for the Knack FAQ centre.
// Vanilla custom element, no dependencies. Everything works without JS (native <details> +
// in-page anchors); this only adds: popular-card click opens the matching question, sticky
// sidebar/mobile-tab active state on scroll, and deep-link opening via the URL hash.
class KnackFaq extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-faq-card]'));
    this.navLinks = Array.from(this.querySelectorAll('[data-faq-nav]'));
    this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    this.cards.forEach((card) => card.addEventListener('click', this.onCardClick.bind(this)));
    this.setupSpy();
    this.onHashChange();
    window.addEventListener('hashchange', this.onHashChange.bind(this));
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onCardClick(event) {
    const id = event.currentTarget.getAttribute('data-target');
    if (!id) return;
    event.preventDefault();
    if (history.replaceState) history.replaceState(null, '', '#' + id);
    this.openTarget(id, true);
  }

  openTarget(id, focus) {
    const details = document.getElementById(id);
    if (!details) return;
    details.open = true;
    details.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (focus) {
      const summary = details.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
    }
  }

  onHashChange() {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash.indexOf('faq-q-') === 0) this.openTarget(hash, false);
  }

  setupSpy() {
    if (!('IntersectionObserver' in window) || !this.groups.length || !this.navLinks.length) return;
    const linkFor = {};
    this.navLinks.forEach((link) => {
      linkFor[link.getAttribute('data-faq-nav')] = link;
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const active = linkFor[entry.target.id];
          this.navLinks.forEach((link) => link.classList.toggle('is-active', link === active));
          // Keep the active tab in view only on the mobile horizontal rail.
          if (active && this.mobileQuery.matches && active.scrollIntoView) {
            active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        });
      },
      { threshold: 0, rootMargin: '-20% 0px -65% 0px' }
    );

    this.groups.forEach((group) => observer.observe(group));
  }
}

if (!customElements.get('knack-faq')) {
  customElements.define('knack-faq', KnackFaq);
}
